import { MantineStyleProps } from '@mantine/core';
import { DynamicUiEntitiesMetadata } from 'src/UI/dynamicUI/types';
import {
  BaseFinancialItem,
  FinancialAsset,
} from 'src/financial/financial-data/hooks/useFinancialItems';
import { AssetDescriptionCell } from 'src/financial/financial-data/tables/AssetDescriptionCell';
import {
  FinancialItemGenericTable,
  formatDate,
  getFinancialItemType,
} from 'src/financial/financial-data/tables/FinancialItemGenericTable';
import { formatMoney } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';

type AccountsTableProps = {
  financialItems: FinancialAsset[];
  metadata: DynamicUiEntitiesMetadata;
  pl?: MantineStyleProps['pl'];
  pr?: MantineStyleProps['pr'];
  titleParts?: string[];
  onSubmit: () => void;
  onRowClick: (clickedItem: BaseFinancialItem) => void;
};

export default function FinancialAssetsTable({
  financialItems,
  metadata,
  pl = 50,
  onRowClick,
}: AccountsTableProps) {
  const currency = useCurrency();
  // Only show Provider Reference Number column if there are InvestmentAndCash items
  const hasInvestmentAndCashItems = financialItems.some(
    item => item.product === 'InvestmentAndCash',
  );

  const fieldsRenderingRules = [
    {
      headerTitle: 'Description',
      sortable: true,
      sortKey: 'description',
      render: (item: FinancialAsset) => (
        <AssetDescriptionCell
          description={item.description}
          isUpdatedAutomatically={item.isUpdatedAutomatically}
          hasFeedData={item.hasFeedData}
        />
      ),
    },
    {
      headerTitle: 'Provider',
      sortable: true,
      sortKey: 'provider',
      render: (item: FinancialAsset) => item.provider,
    },
    ...(hasInvestmentAndCashItems
      ? [
          {
            headerTitle: 'Provider Reference Number',
            sortable: true,
            sortKey: 'providerReferenceNumber',
            render: (item: FinancialAsset) =>
              item.product === 'InvestmentAndCash' && 'providerReferenceNumber' in item
                ? item.providerReferenceNumber
                : undefined,
          },
        ]
      : []),
    {
      headerTitle: 'Type',
      sortable: true,
      sortKey: 'type',
      render: (item: FinancialAsset) => getFinancialItemType(item, metadata),
    },
    {
      headerTitle: 'Last updated',
      sortable: true,
      sortKey: 'valueDate',
      render: (item: FinancialAsset) => formatDate(item.valueDate),
    },
    {
      headerTitle: 'Current value',
      sortable: true,
      sortKey: 'value',
      render: (item: FinancialAsset) =>
        item.value != null ? formatMoney(item.value, currency) : undefined,
    },
  ];

  return (
    <>
      <FinancialItemGenericTable
        financialItems={financialItems}
        fieldsRenderingRules={fieldsRenderingRules}
        pl={pl}
        onRowClick={onRowClick}
      />
    </>
  );
}
